import React, { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { BarChart3, ExternalLink, Trophy } from "lucide-react";
import { buildOffers, effectivePrice, inr, siteColor } from "@/lib/compare";

const OfferRow = ({ o, best, max, onBuyMV }) => {
  const eff = effectivePrice(o);
  const isBest = eff === best;
  return (
    <div className={`rounded-2xl p-3 sm:p-4 border-2 transition-colors ${isBest ? "border-secondary-500 bg-secondary-50" : "border-primary-100 bg-white"}`} data-testid={`compare-row-${o.isMV ? "mv" : o.id}`}>
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl text-white font-extrabold flex items-center justify-center shrink-0" style={{ backgroundColor: o.isMV ? "#7C3AED" : siteColor(o.site) }}>{o.site.charAt(0)}</span>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-primary-900 flex items-center gap-2 flex-wrap">
            {o.site}
            {isBest && <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase bg-secondary-500 text-primary-900 rounded-full px-2 py-0.5"><Trophy className="w-3 h-3" />Best deal</span>}
          </p>
          <p className="text-xs text-primary-500">{o.isMV ? `${inr(o.price)} − ${o.cashback}% cashback` : o.last_checked ? `Checked ${new Date(o.last_checked).toLocaleDateString("en-IN")}` : "Store price"}</p>
        </div>
        <div className="text-right">
          <p className="font-extrabold text-lg text-primary-900">{inr(eff)}</p>
          {o.isMV && <p className="text-[11px] text-primary-400">effective</p>}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-primary-100 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${Math.max(8, (eff / max) * 100)}%`, backgroundColor: isBest ? "#FBBF24" : "#BC9BFB" }} />
        </div>
        {o.isMV ? (
          <button type="button" onClick={onBuyMV} className="text-xs font-bold text-primary-700 hover:underline" data-testid="compare-buy-mv">Buy & earn</button>
        ) : o.url ? (
          <a href={o.url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-primary-700 hover:underline inline-flex items-center gap-1" data-testid={`compare-visit-${o.id}`}>Visit<ExternalLink className="w-3 h-3" /></a>
        ) : null}
      </div>
    </div>
  );
};

export const PriceCompare = ({ product, onBuyMV, defaultOn = true }) => {
  const [on, setOn] = useState(defaultOn);
  const offers = buildOffers(product);
  if (offers.length < 2) return null;
  const effs = offers.map(effectivePrice);
  const best = Math.min(...effs);
  const max = Math.max(...effs);
  const sorted = [...offers].sort((a, b) => effectivePrice(a) - effectivePrice(b));
  return (
    <section className="rounded-3xl bg-primary-50 border-2 border-primary-100 p-4 sm:p-5" data-testid="price-compare-panel">
      <label className="flex items-center justify-between gap-3 cursor-pointer">
        <span className="flex items-center gap-2 font-heading font-bold text-primary-900"><BarChart3 className="w-5 h-5 text-primary-600" />Compare prices across {offers.length} stores</span>
        <Switch checked={on} onCheckedChange={setOn} data-testid="price-compare-toggle" />
      </label>
      {on && (
        <div className="mt-4 space-y-3" data-testid="price-compare-list">
          {sorted.map((o) => <OfferRow key={o.id} o={o} best={best} max={max} onBuyMV={onBuyMV} />)}
          {max > best && <p className="text-sm font-semibold text-primary-700 text-center pt-1" data-testid="price-compare-savings">You save up to {inr(max - best)} by picking the best deal</p>}
        </div>
      )}
    </section>
  );
};
